import React, { useState } from 'react';

/**
 * A component for managing documents
 *
 * @param {Object} props
 * @param {Array} props.documents - The list of documents
 * @param {Function} props.onAddDocument - Function to call when adding a document
 * @param {Function} props.onUpdateDocument - Function to call when updating a document
 * @param {Function} props.onDeleteDocument - Function to call when deleting a document
 * @param {boolean} props.isSaving - Whether a save operation is in progress
 */
const DocumentsSection = ({
  documents,
  onAddDocument,
  onUpdateDocument,
  onDeleteDocument,
  isSaving
}) => {
  const [newDocument, setNewDocument] = useState({ name: '', document: '' });
  const [editingDocument, setEditingDocument] = useState(null);
  const [showAddDocumentForm, setShowAddDocumentForm] = useState(false);

  const handleNewDocumentChange = (e) => {
    const { name, value } = e.target;
    setNewDocument(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddDocument = () => {
    onAddDocument(newDocument);
    setNewDocument({ name: '', document: '' });
    setShowAddDocumentForm(false);
  };

  const handleEditDocument = (document) => {
    setEditingDocument({ ...document });
  };

  const handleEditDocumentChange = (e) => {
    const { name, value } = e.target;
    setEditingDocument(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUpdateDocument = () => {
    onUpdateDocument(editingDocument);
    setEditingDocument(null);
  };

  return (
    <div className="bg-gray-50 p-6 rounded-lg">
      <h3 className="text-lg font-semibold text-gray-700 mb-4">Documents</h3>

      {/* Add New Document Form */}
      <div className="mb-6 p-4 border rounded bg-white">
        {/* Header with Add Button */}
        <div className="flex justify-between items-center mb-4">
          <h4 className="text-md font-medium">Manage Documents</h4>
          <button
            onClick={() => setShowAddDocumentForm(!showAddDocumentForm)}
            className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center"
          >
            {showAddDocumentForm ? (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                </svg>
                Hide Form
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add New Document
              </>
            )}
          </button>
        </div>

        {/* Conditionally render the form */}
        {showAddDocumentForm && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Document Name
              </label>
              <select
                name="name"
                value={newDocument.name}
                onChange={handleNewDocumentChange}
                className="w-full p-2 border rounded"
              >
                <option value="" disabled>Select document type</option>
                <option value="default">default</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Document Content
              </label>
              <textarea
                name="document"
                value={newDocument.document}
                onChange={handleNewDocumentChange}
                className="w-full p-2 border rounded"
                rows="5"
                placeholder="Enter document content"
              ></textarea>
            </div>
            <button
              onClick={handleAddDocument}
              disabled={isSaving || !newDocument.name || !newDocument.document}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-blue-300"
            >
              {isSaving ? 'Adding...' : 'Add Document'}
            </button>
          </div>
        )}
      </div>

      {/* Document List */}
      <div className="space-y-4">
        <h4 className="text-md font-medium">Existing Documents</h4>

        {documents.length === 0 ? (
          <p className="text-gray-500">No documents available.</p>
        ) : (
          <div className="space-y-4">
            {documents.map((doc) => (
              <div key={doc.id} className="p-4 border rounded bg-white shadow-sm">
                {editingDocument && editingDocument.id === doc.id ? (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Document Name
                      </label>
                      <select
                        name="name"
                        value={editingDocument.name}
                        onChange={handleEditDocumentChange}
                        className="w-full p-2 border rounded"
                      >
                        <option value="" disabled>Select document type</option>
                        <option value="default">default</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Document Content
                      </label>
                      <textarea
                        name="document"
                        value={editingDocument.document}
                        onChange={handleEditDocumentChange}
                        className="w-full p-2 border rounded"
                        rows="5"
                      ></textarea>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={handleUpdateDocument}
                        disabled={isSaving}
                        className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-blue-300 text-sm"
                      >
                        {isSaving ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={() => setEditingDocument(null)}
                        className="px-3 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between items-start mb-2">
                      <h5 className="font-medium">{doc.name}</h5>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditDocument(doc)}
                          className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => onDeleteDocument(doc.id)}
                          className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                    <pre className="text-sm text-gray-600 whitespace-pre-wrap bg-gray-50 p-3 rounded-md overflow-x-auto">{doc.document}</pre>
                    <div className="mt-2 text-xs text-gray-400">
                      Last updated: {new Date(doc.updated_at).toLocaleString()}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentsSection;
